import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IonicModule } from '@ionic/angular';
import { range } from 'lodash';
import { HgmSelectComponent, HgmSelectItem } from '../../components/core/hgm-select';
import { valueOr } from '../../util/core';
import { DisplaySlotLocalizer } from '../services/display-slot-localizer';
import { ParamDefDisplaySlotLocalizer } from '../services/param-def-display-slot-localizer';

export class L10nBinding {
    readonly entries: HgmSelectItem[];

    constructor(
        private localizer: ParamDefDisplaySlotLocalizer,
        private l10nArrayIndex: number,
        private onUserChange: () => void,
    ) {
        this.entries = range(this.member.entries.length).map((i) => ({ key: i, name: this.member.entries[i] }));
    }

    get name() {
        return this.member.name;
    }

    get selectionIndex(): number {
        return this.member.index;
    }

    set selectionIndex(value: number) {
        if (value !== this.selectionIndex) {
            this.localizer.setIndex(this.member.name, value);
            this.onUserChange();
        }
    }

    private get member() {
        return this.localizer.localizations[this.l10nArrayIndex];
    }
}

@Component({
    selector: 'unit-selector',
    imports: [CommonModule, HgmSelectComponent, IonicModule],
    template: `
        <ion-item>
            <ion-label>Unit System</ion-label>
            <hgm-select label="Unit System" [value]="l10nSetName()" (valueChange)="onL10nSetNameChange($event)" [items]="l10nSets()" />
        </ion-item>
        @for (binding of l10nBindings(); track binding.name; let last = $last) {
            @if (binding.entries.length > 1) {
                <ion-item [attr.lines]="last ? 'none' : undefined">
                    <ion-label>{{ binding.name }}</ion-label>
                    <hgm-select [label]="binding.name + ' Units'" [(value)]="binding.selectionIndex" [items]="binding.entries" />
                </ion-item>
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnitSelectorComponent {
    readonly userChange = output<void>();

    readonly l10nSetName = computed(() => this.setName());
    readonly l10nBindings = computed(() => this.l10nBindingsSig());
    readonly l10nSets = computed<HgmSelectItem[]>(() =>
        this.localizationSets()
            .map((s) => ({ key: s, name: s }) as HgmSelectItem)
            .concat([{ key: 'Custom', name: 'Custom', disabled: true }]),
    );

    private readonly localizer: ParamDefDisplaySlotLocalizer;

    private readonly setName = signal<string>('');
    private readonly localizationSets = signal<string[]>([]);
    private readonly l10nBindingsSig = signal<L10nBinding[]>([]);

    constructor() {
        const localizer = inject(DisplaySlotLocalizer);
        if (!(localizer instanceof ParamDefDisplaySlotLocalizer)) {
            throw new Error('Logic error');
        }
        this.localizer = localizer;
        this.localizer.ready().pipe(takeUntilDestroyed()).subscribe({ next: this.onL10nChange });
        this.localizer.localizationsChangeObs.pipe(takeUntilDestroyed()).subscribe({ next: this.onL10nChange });
    }

    onL10nSetNameChange(name: string) {
        if (name) {
            this.localizer.localizationSet = name;
            this.userChange.emit();
        }
    }

    private onL10nChange = () => {
        this.setName.set(valueOr(this.localizer.localizationSet, 'Custom'));
        this.localizationSets.set([...this.localizer.localizationSets]);
        this.l10nBindingsSig.set(
            range(this.localizer.localizations.length).map((index) => new L10nBinding(this.localizer, index, () => this.userChange.emit())),
        );
    };
}
